import {MenuItem} from '@szhsin/react-menu'
import classNames from 'classnames'
import {memo, useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import Badge from '../../../components/core/badge'
import {ImgButton} from '../../../components/core/button'
import Dropdown from '../../../components/core/dropdown'
import ExpandableBlock from '../../../components/core/expandableBlock'
import Modal from '../../../components/core/modal'
import Pulse from '../../../components/core/pulse'
import Tooltip from '../../../components/core/tooltip'
import DateInterval from '../../../components/pages/dateInterval'
import ResourceState from '../../../components/pages/resource/ResourceState'
import IncidentsModal from '../../../components/pages/ResourceIncidents'
import os from '../../../data/selectOptions/os.json'
import {HistoryIcon, NewInfoIcon, SwitchArrowIcon} from '../../../icons'
import {routes} from '../../../routes'
import {getAllLimitUsages} from '../../../stores/auth'
import {getServerResource, getServerResourceMetrics, updateServerResource} from '../../../stores/server-monitoring'
import Customer from '../../../utils/customer'
import {convertToSeconds} from '../../../utils/date'
import {getIntervalUnitAndValue} from '../../../utils/intervalManagerService'
import renderSkeletonLoaders from '../../../utils/loader'
import localStorageKeys from '../../../utils/localStorageKeys'
import localStorageManager from '../../../utils/localStorageManager'
import NavigationHelper from '../../../utils/navigation'
import {areArraysEqual} from '../../../utils/object'
import {osIcons} from '../../../utils/osIcons'
import TextHelper from '../../../utils/text'
import MultiForm from '../../services/MultiFormService/MultiForm'
import '../index.scss'
import useHeaderActions from '../UseHeaderActions'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../../utils/env'

const SERVER_MONITORING = 'server_monitoring'

const AutoRefresh = memo(({className, isAutoRefreshEnabled, setIsAutoRefreshEnabled, isDarkMode, refreshText}) => {
    const handleAutorefresh = (e) => {
        const isEnabled = e.target.checked
        setIsAutoRefreshEnabled(isEnabled)
        localStorageManager.set(localStorageKeys.IS_SERVER_AUTHOREFRESH_ENABLED, isEnabled)
    }

    return (
        <div className={classNames('box-header', {[className]: !!className})}>
            <span className={classNames('left-switch', {dark: isDarkMode})}>{refreshText}</span>
            <span>
                <label className="switch">
                    <input type="checkbox" onChange={handleAutorefresh} checked={isAutoRefreshEnabled} />
                    <span className={classNames('slider round', {off: !isAutoRefreshEnabled})} />
                </label>
            </span>
        </div>
    )
})

const PageHeader = memo(({
    serverResourceProps,
    isButtonLoading,
    metricProps,
    modalsProps,
    activeModal
}) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const text = useNamespaceTranslations('server-monitoring')
    const intervalUnits = useNamespaceTranslations('interval-units')?.interval_units
    const serverMetrics = useNamespaceTranslations('server-metrics')?.server_metrics
    const isFetching = useSelector((state) => state.resources.isFetching)
    const isDarkMode = useSelector((state) => state.sidebar.isDarkMode)

    const {
        serverGroupedMetricValues,
        activeMetrics,
        setActiveMetrics,
        setFilteredMetrics,
        commonMetricsInfo,
        serverMetricValues,
        isMetricsDataRetrieving
    } = metricProps

    const {
        serverResource,
        serverResources,
        selectedOsOption,
        id,
        isFirstLoad,
        isResourceFetching
    } = serverResourceProps

    const {
        setInstructionModalContent,
        osInstructionCodes,
        toggleInstructionModal,
        showUpdateWarning,
        setShowUpdateWarning
    } = modalsProps

    const {
        handleSelectedMetricTypes,
        selectedMetricTypes,
        getActions,
        usageLimits,
        renderIntervalSettings,
        isIntervalLimitOverExceeded,
        handleIncidentMenu,
        state,
        setState
    } = useHeaderActions({isFetching: isResourceFetching, serverResource, showStatusBtns: false})

    const dashboardPath = `/server-monitoring/${id}`
    const lang = localStorage.getItem(localStorageKeys.LANGUAGE)
    const isUsageLimitExceeded = useMemo(() =>
        Object.values(selectedMetricTypes)?.some(metric => {
            return metric?.usage > usageLimits?.[metric?.key]?.value && metric?.child_items?.length > 0
        }),
    [selectedMetricTypes, usageLimits])

    const isProcessing = isResourceFetching || isMetricsDataRetrieving?.[serverMetrics.cpu_total_usage.value]
    const serverInterval = getIntervalUnitAndValue(serverResource?.settings?.interval, intervalUnits)
    const serverIntervalDetails = serverInterval?.unitValue
        ? `${serverInterval.unitValue} ${serverInterval?.unitLabel + (lang === 'en' ? TextHelper.pluralize(serverInterval.unitValue) : '')}`
        : text.not_available

    const IconComponent = osIcons?.[selectedOsOption?.icon] || os.centos.icon

    const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(
        localStorageManager.get(localStorageKeys.IS_SERVER_AUTHOREFRESH_ENABLED)
    )

    const getMetricsDataForFilters = useCallback((data) => {
        const {startDate, endDate, interval, showLoader} = data
        const query = `start_date=${startDate}&end_date=${endDate}&interval=${interval}`

        dispatch(getServerResourceMetrics(serverMetrics, showLoader, query, null,
            !activeModal.metric_type || activeModal.metric_type !== serverMetrics.processes_unified.value ? 1 : null))
        document.querySelectorAll('.apexcharts-reset-icon').forEach((item) => item.click())
    }, [dispatch, activeModal.metric_type])

    const handleUpdateServer = useCallback(async() => {
        const updatedPayload = {
            ...serverResource,
            metric_types_basic: selectedMetricTypes?.basic?.child_items?.map((item) => item.value),
            metric_types_advanced: selectedMetricTypes?.advanced?.child_items?.map((item) => item.value),
            metric_types_complex: selectedMetricTypes?.complex?.child_items?.map((item) => item.value),
            interval: state.isAdvancedEnabled && state.intervalValues.interval_value && state.intervalValues.interval_unit ?
                convertToSeconds(state.intervalValues.interval_value, state.intervalValues.interval_unit?.value, intervalUnits) : 0
        }

        const result = await dispatch(updateServerResource(updatedPayload, true, serverResource?.id, true))
        const isUpdateWarningNeeded = !areArraysEqual(serverResource?.grouped_metric_types?.basic || [],
            updatedPayload?.metric_types_basic || []) ||
        !areArraysEqual(serverResource?.grouped_metric_types?.advanced || [], updatedPayload?.metric_types_advanced  || []) ||
        !areArraysEqual(serverResource?.grouped_metric_types?.complex || [], updatedPayload?.metric_types_complex || [])

        if (result?.success) {
            isUpdateWarningNeeded && setShowUpdateWarning(true)
            dispatch(getServerResource(serverMetrics, false, serverResource?.id, navigate, false, false))
            setState(prev => ({...prev, manualUpdate: true}))
            dispatch(getAllLimitUsages())
        }
    }, [dispatch, selectedMetricTypes, serverResource, setShowUpdateWarning, state.isAdvancedEnabled, state.intervalValues, state.manualUpdate])

    const handleFilter = useCallback(({isReset = false}) => {
        if (!isReset) {
            setFilteredMetrics(activeMetrics)
            return
        }
        const updatedMetricValues = serverMetricValues?.filter((metric) => serverGroupedMetricValues?.includes(metric?.value))
        setActiveMetrics(updatedMetricValues)
        setFilteredMetrics(updatedMetricValues)
    }, [setFilteredMetrics,
        activeMetrics,
        setActiveMetrics,
        serverGroupedMetricValues,
        serverMetricValues])

    const resetFilters = useCallback(() => {
        dispatch(getServerResourceMetrics(serverMetrics, selectedMetricTypes, true))
    }, [dispatch, selectedMetricTypes])

    const renderServerResourcesMenu = useCallback(() => (
        isFetching ? renderSkeletonLoaders(1, 'switch-skeleton') : (
            <Dropdown
                menuButton={<span><SwitchArrowIcon /></span>}
                className={classNames('resources-menu', {
                    dark: isDarkMode
                })}
                align="center"
            >
                {serverResources?.map((resource, index) => (
                    <MenuItem
                        className={classNames('resources-menu-item', {
                            active: serverResource?.id === resource?.id
                        })}
                        onClick={() => navigate(NavigationHelper.getNavigationUrl(
                            NavigationHelper.getLocalizedPath(routes.server_resource.paths),
                            ':id',
                            resource?.id))}
                        key={index}
                    >
                        {resource?.name}
                    </MenuItem>
                ))}
            </Dropdown>
        )
    ), [isFetching,
        isDarkMode,
        serverResources,
        serverResource,
        navigate])

    const handleInstructionClick = useCallback(() => {
        setInstructionModalContent({[selectedOsOption?.type || os.centos.type]: osInstructionCodes?.[selectedOsOption?.type || os.centos.type]})
        toggleInstructionModal()
    }, [setInstructionModalContent, selectedOsOption, toggleInstructionModal, osInstructionCodes, serverResource, os])

    useEffect(() => {
        const interval = setInterval(() => {
            if (serverResource?.id) {
                dispatch(getServerResource(serverMetrics, false, serverResource?.id, navigate, true, true))
            }
        }, envConfig.liveUpdateTime)

        return () => clearInterval(interval)
    }, [serverResource])

    useEffect(() => {
        setState(prev => ({...prev, manualUpdate: true}))
    }, [serverResource?.id])

    return (
        <>
            {state.activeIncidentsModalId &&   <Modal
                isOpen={state.activeIncidentsModalId}
                contentClassName="overflow-auto"
                id="large-modal"
                title={text.incidents}
                closeModal={() => handleIncidentMenu(serverResource?.id)}
            >
                <IncidentsModal
                    resourceId={serverResource?.id}
                    resourceType='ServerResource'
                />
            </Modal>}
            <div className="monitoring-sub-title justify-content-flex-end">
                {isFirstLoad ? renderSkeletonLoaders(1, 'switch-skeleton') : (
                    <AutoRefresh
                        isAutoRefreshEnabled={isAutoRefreshEnabled}
                        setIsAutoRefreshEnabled={setIsAutoRefreshEnabled}
                        isDarkMode={isDarkMode}
                        refreshText={text.auto_refresh}
                    />
                )}
            </div>
            <div className="monitoring-sub-title">
                {isResourceFetching || isFirstLoad ?
                    renderSkeletonLoaders(1) : (
                        <span className="resource-name-field">
                            <span className={classNames('d-flex align-items-center', {
                                'gap-half': !!serverResource?.settings?.interval
                            })}>
                                <ResourceState
                                    tooltipText={
                                        !serverResource?.settings?.interval
                                            ? text.disabled_interval_warning_text
                                            : serverResource?.has_open_incidents
                                                ? text.down
                                                : text.up
                                    }
                                    icon={serverResource?.settings?.interval && <Pulse color={serverResource?.has_open_incidents ? 'red' : ''}/>}
                                />
                                <span className={classNames('resource-name', {
                                    dark: isDarkMode,
                                    'ml-8': !!serverResource?.settings?.interval ||
                                    (!serverResource?.settings?.interval && !serverResource?.setup_complete)
                                })}>
                                    {!serverResource?.setup_complete && <span className={classNames({
                                        'not-completed-resource': !!serverResource?.settings?.interval,
                                        'mr-4': !selectedOsOption
                                    })}><ResourceState tooltipText={text.warning_text}/></span>}
                                    {selectedOsOption ?
                                        <Tooltip content={selectedOsOption?.label} trigger='mouseenter' placement='bottom' noButton>
                                            <span className='os-icon'><IconComponent height={selectedOsOption?.height || 28}/></span>
                                        </Tooltip> : ''}
                                    <h2>{serverResource?.name}</h2>
                                    <div className="resource-switcher">
                                        <span className="switch-icon">
                                            {renderServerResourcesMenu()}
                                        </span>
                                    </div>
                                    <Tooltip
                                        content={serverResource?.description}
                                        trigger="click"
                                        placement="right">
                                        <NewInfoIcon width="22px" height="22px" />
                                    </Tooltip>
                                </span>
                            </span>
                            <span className={classNames('resource-host', {
                                dark: isDarkMode
                            })}>
                                {isProcessing ?
                                    renderSkeletonLoaders(1, 'text-skeleton') :
                                    (commonMetricsInfo?.host ?
                                        `${commonMetricsInfo?.host} / ${commonMetricsInfo?.ip}` :
                                        '')}
                            </span>
                            <span className={classNames('resource-host', {
                                dark: isDarkMode
                            })}>
                                {isProcessing ?
                                    renderSkeletonLoaders(1, 'text-skeleton') :
                                    serverResource?.timezone}
                            </span>
                        </span>
                    )}
                <span className="search-filter-actions">
                    {Customer.isOwnTeam() && getActions({
                        state: dashboardPath,
                        toggleInstructionModal: handleInstructionClick,
                        activeMetrics,
                        setActiveMetrics,
                        serverGroupedMetricValues,
                        serverMetricValues,
                        onFilter: handleFilter
                    })}
                </span>
            </div>
            <span className="search-filter-actions responsive-actions">
                {Customer.isOwnTeam() && getActions({
                    state: dashboardPath,
                    toggleInstructionModal: handleInstructionClick,
                    activeMetrics,
                    setActiveMetrics,
                    serverGroupedMetricValues,
                    serverMetricValues,
                    setFilteredMetrics,
                    onFilter: handleFilter
                })}
            </span>
            <ExpandableBlock blockTitle={<span className="d-flex gap-half">
                {text.metric_types}
                {showUpdateWarning && (
                    <ResourceState
                        tooltipText={
                            <span>
                                {text.warning_text_after_update}
                                <span className="link-text" onClick={toggleInstructionModal}>{text.here_to_update}</span>
                                {text.restart_agent_to_view_metrics}
                            </span>
                        }
                        interactive
                    />

                )}
            </span>}>
                <MultiForm
                    monitoringType={text.steps.metrics.type}
                    monitoringTypeData={text.steps.metrics.items}
                    selectedMetricTypes={selectedMetricTypes}
                    onChange={handleSelectedMetricTypes}
                    selectedItem={selectedOsOption}
                    updateServer={handleUpdateServer}
                    isButtonDisabled={state.isButtonDisabled || isUsageLimitExceeded || (isIntervalLimitOverExceeded && !!state.isAdvancedEnabled)}
                    isButtonLoading={isButtonLoading}
                    metricsClassName="dashboard-metric-types-container"
                    advancedSettingsContainer={renderIntervalSettings()}
                    isExpandable
                />
            </ExpandableBlock>
            <div className={classNames('monitoring-search', {
                dark: isDarkMode
            })}>
                <span className='monitoring-search-left'>
                    <div className='server-details-box'>
                        {isResourceFetching ?
                            renderSkeletonLoaders(1, 'switch-row-skeleton') :
                            <>
                                {text.interval_based_text}
                                {serverResource?.settings?.interval
                                    ? serverIntervalDetails
                                    : text.not_available}
                                <Badge color={!serverResource?.settings?.interval ? text.warning_status : text.success_status}>
                                    {!serverResource?.settings?.interval ? text.disabled_status : text.enabled_status}
                                </Badge>
                            </>}
                    </div>
                    <div className='search-filter-actions'>
                        <span className='selected-actions'>
                            <ImgButton className={classNames({
                                'light-bg': !isDarkMode
                            })} onClick={() => handleIncidentMenu(serverResource?.id)}
                            disabled={isResourceFetching}>
                                <HistoryIcon/>
                                {text.incidents}
                            </ImgButton>
                        </span>
                    </div>
                </span>
                <span className="monitoring-search-right">
                    {isFirstLoad ? renderSkeletonLoaders(1) : (
                        <DateInterval
                            callbackFn={getMetricsDataForFilters}
                            type={SERVER_MONITORING}
                            resetFn={resetFilters}
                            isAutoRefreshEnabled={isAutoRefreshEnabled}
                        />
                    )}
                </span>
            </div>
        </>
    )
})

export default PageHeader
